import React, { FormEvent, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel, Grid, Typography } from '@material-ui/core'

import { LabeledInput } from '../../LabeledInput'
import { SyncStartTableRow } from '../../SyncStartTableRow'
import { DefaultRadioGroup } from '../../DefaultRadioGroup'

import { useOzonForm } from './hooks/useOzonForm'
import { useMessages } from '../../../hooks/snackbarHooks'
import { useApi, useLogic } from '../../../hooks/storeHook'
import { useRadioGroupStyles } from '../../../hooks/radioGroupStylesHook'
import { useSettingsPageStyles } from '../../../hooks/settingsPageStylesHook'

import { appRoutes } from '../../../common/appRoutes'

import { OzonDeliveryType } from '../../../server/mpsklad_core/Entity/Ozon/OzonDeliveryType'
import { EditOzonAccountModel } from '../../../server/mpsklad_core/Models/EditOzonAccountModel'
import { CommissionsRecordingType } from '../../../server/mpsklad_core/Entity/Base/CommissionsRecordingType'
import { isAppTypeMpSklad } from '../../../common/isAppTypeMpSklad'
import { makeMpCardAccountUrl } from '../../../common/mpCardRoutes'
import { IntegrationType } from '../../../server/mpsklad_core/Entity/IntegrationType'
import { useOzonAccountOptional } from '../../../hooks/routeHooks'

export const OzonApiSettings =
  observer(() => {
    const classes = useSettingsPageStyles()
    const radioGroupClasses = useRadioGroupStyles()

    const getDeliveryTypeClasses =
      (checked: boolean) =>
        clsx(radioGroupClasses.radioButton, checked && radioGroupClasses.selectedRadioButton)

    const {showSuccess, showError} = useMessages()

    const logic = useLogic()
    const {userSync: {setOzonSyncStartDate}} = useApi()

    const account = useOzonAccountOptional()
    const history = useHistory()

    const {
      isLoading,
      setIsLoading,
      deliveryType,
      setDeliveryType,
      apiKey,
      setApiKey,
      clientId,
      setClientId,
      name,
      setName
    } = useOzonForm(account)

    const deliveryTypeOptions =
      useMemo(() => [{
        value: OzonDeliveryType.FBS,
        label: 'Продажа со своего склада (FBS)'
      }, {
        value: OzonDeliveryType.FBO,
        label: 'Продажа со склада Ozon (FBO)'
      }, {
        value: OzonDeliveryType.RFBS,
        label: 'Продажа со своего склада и доставка силами продавца (realFBS)'
      }, {
        value: OzonDeliveryType.RFBS_Express,
        label: 'Схема rFBS Express с доставкой в пункт выдачи'
      }], [])

    const onSubmit = async (e: FormEvent) => {
      e.preventDefault()

      if (!apiKey || !clientId || !name) {
        showError('Необходимо заполнить все обязательные поля.')
        return
      }

      setIsLoading(true)

      try {
        const formData = {
          deliveryType,
          name: name || undefined,
          apiKey,
          clientId: parseInt(clientId),
          // TODO: Split models according to UI and actual data being used, don't just re-send the same stuff
          isUpdatePrices: account?.isUpdatePrices ?? false,
          isGeneratingPayments: account?.isGeneratingPayments ?? false,
          isUsingCommissions: account?.isUsingCommissions ?? false,
          ordersCommissionsRecording:
            account?.ordersCommissionsRecording ?? CommissionsRecordingType.Nothing,
          documentsCommissionsRecording:
            account?.documentsCommissionsRecording ?? CommissionsRecordingType.Nothing,
          msSalesReturnStoreId: account?.msSalesReturnStoreId
        } satisfies Omit<EditOzonAccountModel, 'id'>

        if (account) {
          await logic.editOzonAccount({id: account.id, ...formData})
        } else {
          const newAccount = await logic.createOzonAccount({id: 0, ...formData})

          isAppTypeMpSklad()
          ? history.push(appRoutes.Store.settings.api.makeUrl({accountId: newAccount.id, integration: 'Ozon'}))
          : history.push(makeMpCardAccountUrl(IntegrationType.Ozon, newAccount.id))
        }

        showSuccess('Сохранено!')
      } catch (ex) {
        console.error('Failed to save Ozon account', ex)
        showError('Ошибка при сохранении аккаунта')
      } finally {
        setIsLoading(false)
      }
    }

    return (
      <Box className={classes.pageContainer}>
        {isAppTypeMpSklad() && <Typography className={classes.header}>Настройки API</Typography>}
        <form onSubmit={onSubmit}>
          <Grid container spacing={3}>
            {/* Left column */}
            <Grid item style={account ? {minWidth: '50%'} : {}} className={classes.wrapper}>
              <Grid container spacing={3}>
                {/* API Token and API Key in one row */}
                <Grid item xs={12}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                        <Grid item className={classes.root} xs={12}>
                          <LabeledInput
                            required
                            id="clientId"
                            label="Id клиента"
                            placeholder="12227"
                            value={clientId}
                            onChange={setClientId}
                          />
                        </Grid>

                        <Grid item className={classes.root} xs={12}>
                          <LabeledInput
                            required
                            id="apiKey"
                            label="API ключ"
                            placeholder="be0813e9"
                            value={apiKey}
                            onChange={setApiKey}
                          />

                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Schema of Work */}
                {isAppTypeMpSklad() && <Grid item xs={12}>
                  <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                    <Grid item className={classes.root} xs={12}>
                      <DefaultRadioGroup
                        label="Схема работы"
                        options={deliveryTypeOptions}
                        value={deliveryType}
                        onChange={setDeliveryType}
                        getOptionLabelClassName={() => radioGroupClasses.radioLabel}
                        getOptionClassName={getDeliveryTypeClasses}
                        name="ModelType"
                      />
                    </Grid>
                  </Grid>
                </Grid>}

                {/* Account Name */}
                <Grid item xs={12}>
                  <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                    <Grid item className={classes.root} xs={12}>
                      <LabeledInput
                        required
                        id="accountName"
                        label="Название аккаунта"
                        placeholder="Ozon 1234"
                        value={name}
                        onChange={setName}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Right column */}
            {account &&
             <Grid item xs={12} md={6}>
               <Grid container spacing={3}>
                 {
                   account.ordersTrackStartUnix &&
                   <Grid item xs={12}>
                     <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                       <Grid item className={classes.root} xs={12}>
                         <Box>
                           <FormLabel component="legend" className={classes.boldLabel}>
                             Дата начала синхронизации заказов
                           </FormLabel>
                         </Box>

                         <SyncStartTableRow account={account} onSubmit={setOzonSyncStartDate}/>

                         <FormHelperText className={classes.helperText}>
                           Будут синхронизированы заказы, созданные позже этой даты.
                         </FormHelperText>
                       </Grid>
                     </Grid>
                   </Grid>
                 }
               </Grid>
             </Grid>
            }
          </Grid>

          <Box display="flex">
            <button
              type="submit"
              className={clsx(classes.saveButton, 'default-button')}
              disabled={isLoading}
            >
              <p>{isLoading ? 'Сохраняем...' : 'Сохранить'}</p>
            </button>
          </Box>
        </form>
      </Box>
    )
  })